import Phaser from 'phaser';
import { SCENES } from '../types/game';
import { AssetLoader } from '../utils/AssetLoader';
import { AudioManager } from '../utils/AudioManager';

export class HomeScene extends Phaser.Scene {
  private userData: any = null;
  private playButton?: Phaser.GameObjects.Image;
  private leaderboardButton?: Phaser.GameObjects.Image;
  private bestiaryButton?: Phaser.GameObjects.Image;
  private upgradesButton?: Phaser.GameObjects.Text;
  private eventButton?: Phaser.GameObjects.Text;
  private userText?: Phaser.GameObjects.Text;

  constructor() {
    super({ key: SCENES.HOME });
  }

  init(data?: any) {
    if (data && data.user) {
      this.userData = data.user;
    }
  }

  preload() {
    // Load all game assets including home screen assets
    AssetLoader.preloadGameAssets(this);
    
    // Load audio assets
    AudioManager.getInstance().preloadAudio(this);
  }

  create() {
    const { width, height } = this.cameras.main;

    // Play main menu music
    const currentMusic = AudioManager.getInstance().getCurrentMusicKey();
    const isMusicPlaying = AudioManager.getInstance().isMusicPlaying();
    
    if (currentMusic !== 'main-menu-music' || !isMusicPlaying) {
      AudioManager.getInstance().playMainMenuMusic(this);
    }

    // Background
    const background = this.add.image(width / 2, height / 2, 'main_menu');
    background.setDisplaySize(width, height);

    // Play button
    this.playButton = this.add.image(width / 2, 390, 'play_btn');
    this.playButton.setInteractive({ cursor: 'pointer' });

    this.playButton.on('pointerdown', () => this.startGame());
    this.playButton.on('pointerover', () => this.playButton!.setScale(1.1).setTint(0xdddddd));
    this.playButton.on('pointerout', () => this.playButton!.setScale(1.0).clearTint());

    // Leaderboard button
    this.leaderboardButton = this.add.image(width / 2, 450, 'leaderboard_btn');
    this.leaderboardButton.setInteractive({ cursor: 'pointer' });

    this.leaderboardButton.on('pointerdown', () => this.scene.start(SCENES.LEADERBOARD, { user: this.userData }));
    this.leaderboardButton.on('pointerover', () => this.leaderboardButton!.setScale(1.1).setTint(0xdddddd));
    this.leaderboardButton.on('pointerout', () => this.leaderboardButton!.setScale(1.0).clearTint());

    // Bestiary button
    this.bestiaryButton = this.add.image(width / 2, 520, 'bestiary_btn');
    this.bestiaryButton.setInteractive({ cursor: 'pointer' });

    this.bestiaryButton.on('pointerdown', () => this.scene.start(SCENES.BESTIARY, { user: this.userData }));
    this.bestiaryButton.on('pointerover', () => this.bestiaryButton!.setScale(1.1).setTint(0xdddddd));
    this.bestiaryButton.on('pointerout', () => this.bestiaryButton!.setScale(1.0).clearTint());

    // Upgrades button (rendered as shiny text for now)
    this.upgradesButton = this.add.text(width / 2, 580, 'UPGRADES', {
      fontSize: '24px',
      fontFamily: 'Minecraft',
      color: '#c084fc',
      fontStyle: 'bold',
      stroke: '#6d28d9',
      strokeThickness: 4
    }).setOrigin(0.5).setInteractive({ cursor: 'pointer' });

    this.upgradesButton.on('pointerdown', () => this.openUpgrades());
    this.upgradesButton.on('pointerover', () => this.upgradesButton!.setScale(1.1).setColor('#e9d5ff'));
    this.upgradesButton.on('pointerout', () => this.upgradesButton!.setScale(1.0).setColor('#c084fc'));

    this.eventButton = this.add.text(130, 580, 'EVENT', {
      fontSize: '24px',
      fontFamily: 'Minecraft',
      color: '#f59e0b',
      fontStyle: 'bold',
      stroke: '#92400e',
      strokeThickness: 4
    });
    this.eventButton.setOrigin(0.5);
    this.eventButton.setInteractive({ cursor: 'pointer' });
    this.eventButton.on('pointerdown', () => this.scene.start(SCENES.EVENT, { user: this.userData }));
    this.eventButton.on('pointerover', () => this.eventButton!.setScale(1.1).setColor('#fcd34d'));
    this.eventButton.on('pointerout', () => this.eventButton!.setScale(1).setColor('#f59e0b'));

    // Listen for user updates
    this.events.on('userUpdated', this.onUserUpdated, this);
  }

  private openUpgrades() {
    if (!this.userData) {
      this.events.emit('showAuthModal');
      return;
    }
    this.events.emit('showUpgradeModal');
  }

  private onUserUpdated(user: any) {
    this.userData = user;
  }

  private startGame() {
    // Validate authentication for Kingsomni
    if (!this.userData) {
      this.events.emit('showAuthModal');
      return;
    }
    
    // User is authenticated - proceed with game
    const seed = this.generateSeed();
    AudioManager.getInstance().fadeFromMainMenuToBattle(this);
    
    this.scene.start(SCENES.GAME, {
      user: this.userData,
      seed: seed,
    });
  }

  private generateSeed(): string {
    const timestamp = Date.now();
    const random = Math.random().toString(36).substring(2);
    return `${timestamp}_${random}`;
  }

  public updateUser(user: any) {
    this.userData = user;
  }

  shutdown() {
    this.events.off('userUpdated', this.onUserUpdated, this);
  }
}
